/**
 * SlashSkillEffect — Blades tree slash-path active: wide arc attack.
 *
 * 120-degree cone AoE melee attack hitting all enemies in the arc
 * simultaneously. Single crit roll per swing applies to all targets.
 * Medium cooldown tier.
 *
 * Supports upgrade keys:
 * - Extended Arc: `arcDegrees` (additive, widens cone),
 *   `trailLingerMs` (additive, trail hitbox duration)
 * - Residual Edge: `afterimageDamageMultiplier` (additive, fraction of
 *   hit damage dealt by afterimage zone)
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Slash
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SlashDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startArcAttack(attackType: string, arcDegrees: number): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SlashConfig {
  readonly cooldownMs: number;
  readonly baseArcDegrees: number;
  readonly baseDamageMultiplier: number;
}

const DEFAULT_CONFIG: SlashConfig = {
  cooldownMs: 800,                  // medium cooldown tier
  baseArcDegrees: 120,              // 120-degree cone
  baseDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SlashSkillEffect implements SkillEffect {
  readonly skillId = 'slash';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SlashDeps;
  private readonly config: SlashConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _totalEnemiesHit = 0;
  private _resolvedArcDegrees = 120;
  private _resolvedDamageMultiplier = 1.0;
  private _resolvedTrailLingerMs = 0;
  private _resolvedAfterimageDamageMultiplier = 0;

  constructor(deps: SlashDeps, config: Partial<SlashConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get attacksFired(): number { return this._attacksFired; }
  get totalEnemiesHit(): number { return this._totalEnemiesHit; }
  get resolvedArcDegrees(): number { return this._resolvedArcDegrees; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }
  get resolvedTrailLingerMs(): number { return this._resolvedTrailLingerMs; }
  get resolvedAfterimageDamageMultiplier(): number { return this._resolvedAfterimageDamageMultiplier; }

  get cooldown(): number { return this._cooldownRemaining; }
  get resolvedCooldownMs(): number { return this.config.cooldownMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve arc width (base + Extended Arc bonus)
    const arcBonus = this.deps.resolver.getAdditiveEffect('slash', 'arcDegrees');
    this._resolvedArcDegrees = this.config.baseArcDegrees + arcBonus;

    // Resolve damage multiplier
    this._resolvedDamageMultiplier = this.config.baseDamageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('slash', 'damage');

    // Resolve trail linger (Extended Arc)
    this._resolvedTrailLingerMs = this.deps.resolver.getAdditiveEffect('slash', 'trailLingerMs');

    // Resolve afterimage damage fraction (Residual Edge)
    this._resolvedAfterimageDamageMultiplier =
      this.deps.resolver.getAdditiveEffect('slash', 'afterimageDamageMultiplier');

    // Fire the arc attack — returns number of enemies hit
    const enemiesHit = this.deps.combat.startArcAttack('slash', this._resolvedArcDegrees);
    this._totalEnemiesHit += enemiesHit;
    this._attacksFired++;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
